import { useState, useEffect } from 'react'
import { Button } from '@/ui/button'
import { Badge } from '@/ui/badge'
import { MaterialIcon } from '@/ui/material-icon'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/ui/card'
import styles from './GuidedTour.module.scss'
import navigation from '@/assets/guidedimage/navigation.svg'
import guided from '@/assets/guidedimage/guided.svg'

const TOUR_STORAGE_KEY = 'akashic_adp_tour_completed'

/* ─── Step content ──────────────────────────────────────────────── */

function AssetsStepContent() {
  return (
    <div className={styles.iconTile}>
      <img src={guided} alt="Guided Illustration" />
    </div>
  )
}

function NavigationStepContent() {
  return (
    <div className={styles.navIllustration}>
      <img src={navigation} alt="Navigation Illustration" />
    </div>
  )
}

function PipelineStatusStepContent() {
  return (
    <div className={styles.statusTable}>
      <div className={styles.statusRow}>
        <span className={styles.statusLabel}>Status</span>
        <span className={styles.statusColon}>:</span>
        <Badge variant="outline" className={styles.statusNotStarted}>
          ● Not Started
        </Badge>
      </div>
      <div className={styles.statusRow}>
        <span className={styles.statusLabel}>Duration</span>
        <span className={styles.statusColon}>:</span>
        <span className={styles.statusValue}>00:00:00</span>
      </div>
      <div className={styles.statusRow}>
        <span className={styles.statusLabel}>Run Start</span>
        <span className={styles.statusColon}>:</span>
        <span className={styles.statusValue}>19/02/2026 &nbsp; 00:00:00</span>
      </div>
      <div className={styles.statusRow}>
        <span className={styles.statusLabel}>Run End</span>
        <span className={styles.statusColon}>:</span>
        <span className={styles.statusValue}>19/02/2026 &nbsp; 00:00:00</span>
      </div>
    </div>
  )
}

/* ─── SVG Triangle pointer ──────────────────────────────────────── */

type Side = 'top' | 'bottom' | 'left' | 'right'

function Pointer({ side }: { side: Side }) {
  const W = 24
  const H = 14
  const isVertical = side === 'top' || side === 'bottom'
  const svgW = isVertical ? W : H
  const svgH = isVertical ? H : W

  let points = ''
  if (side === 'top')    points = `${W / 2},0 0,${H} ${W},${H}`
  if (side === 'bottom') points = `0,0 ${W},0 ${W / 2},${H}`
  if (side === 'left')   points = `0,${W / 2} ${H},0 ${H},${W}`
  if (side === 'right')  points = `0,0 ${H},${W / 2} 0,${W}`

  const shadowTransform =
    side === 'top'    ? 'translate(0, 1)'  :
    side === 'bottom' ? 'translate(0, -2)' :
    side === 'left'   ? 'translate(2, 0)'  :
                        'translate(-2, 0)'

  return (
    <svg
      className={`${styles.pointer} ${styles[`pointer-${side}`]}`}
      width={svgW}
      height={svgH}
      viewBox={`0 0 ${svgW} ${svgH}`}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <polygon
        points={points}
        fill="rgba(0,0,0,0.08)"
        transform={shadowTransform}
      />
      <polygon points={points} fill="#ffffff" />
    </svg>
  )
}

/* ─── Step definitions ──────────────────────────────────────────── */

type StepDef = {
  title: string
  description: string
  content: React.ReactNode
  pointerSide: Side
  pointerOffset: string
  position: Partial<Record<'top' | 'bottom' | 'left' | 'right', string>>
  transform?: string
}

const STEPS: StepDef[] = [
  {
    title: 'Module Guide (Assets)',
    description:
      'Choose from various Data Sources to begin setting up your pipeline. You can drag and drop multiple data sources from here onto your workspace.',
    content: <AssetsStepContent />,
    pointerSide: 'left',
    pointerOffset: '32px',
    position: { top: '125px', left: '13%' },
  },
  {
    title: 'Navigation',
    description:
      'Controls for your job/pipelines. You can validate, run, schedule, import, export pipelines for better control of your data.',
    content: <NavigationStepContent />,
    pointerSide: 'top',
    pointerOffset: '50%',
    position: { top: '125px', left: '32%' },
    transform: 'translateX(-50%)',
  },
  {
    title: 'Pipeline status',
    description:
      'View Details related to the pipeline status, Run history, Edit details etc.',
    content: <PipelineStatusStepContent />,
    pointerSide: 'top',
    pointerOffset: 'calc(100% - 52px)',
    position: { top: '125px', right: '5px' },
  },
]

/* ─── Component ─────────────────────────────────────────────────── */

type GuidedTourProps = {
  forceShow?: boolean
  onFinish?: () => void
}

export function GuidedTour({ forceShow = false, onFinish }: GuidedTourProps) {
  const [visible, setVisible] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [animating, setAnimating] = useState(false)

  useEffect(() => {
    const alreadySeen = localStorage.getItem(TOUR_STORAGE_KEY)
    if (forceShow || !alreadySeen) {
      const t = setTimeout(() => setVisible(true), 500)
      return () => clearTimeout(t)
    }
  }, [forceShow])

  const dismiss = () => {
    localStorage.setItem(TOUR_STORAGE_KEY, 'true')
    setVisible(false)
    onFinish?.()
  }

  const goTo = (index: number) => {
    if (animating) return
    setAnimating(true)
    setTimeout(() => {
      setCurrentStep(index)
      setAnimating(false)
    }, 160)
  }

  const next = () => {
    if (currentStep < STEPS.length - 1) goTo(currentStep + 1)
    else dismiss()
  }

  if (!visible) return null

  const step = STEPS[currentStep]
  const isLast = currentStep === STEPS.length - 1

  const wrapperStyle: React.CSSProperties = {
    ...step.position,
    ...(step.transform ? { transform: step.transform } : {}),
    '--pointer-offset': step.pointerOffset,
  } as React.CSSProperties

  return (
    <div className={styles.overlay} onClick={dismiss} aria-hidden>
      <div
        className={`${styles.wrapper} ${animating ? styles.wrapperFading : ''}`}
        style={wrapperStyle}
        onClick={e => e.stopPropagation()}
      >
        {/* Triangle pointer */}
        <Pointer side={step.pointerSide} />

        {/* ── shadcn Card replaces the raw .card div ── */}
        <Card
          className={styles.card}
          role="dialog"
          aria-modal
          aria-labelledby="guided-tour-title"
        >
          {/* ── Close button — MaterialIcon is already in your /ui folder ── */}
          <button
            type="button"
            className={styles.closeBtn}
            onClick={dismiss}
            aria-label="Close tour"
          >
            <MaterialIcon name="close" size={18} />
          </button>

          <CardHeader className={styles.cardHeader}>
            <p className={styles.stepCounter}>
              {currentStep + 1} / {STEPS.length}
            </p>
            <CardTitle id="guided-tour-title" className={styles.title}>
              {step.title}
            </CardTitle>
          </CardHeader>

          <CardContent className={styles.cardContent}>
            <div className={styles.contentArea}>{step.content}</div>
            <p className={styles.description}>{step.description}</p>
          </CardContent>

          <CardFooter className={styles.cardFooter}>
            <div className={styles.actions}>
              <Button
                type="button"
                variant="outline"
                className={styles.skipBtn}
                onClick={dismiss}
              >
                Skip
              </Button>
              <Button
                type="button"
                className={styles.nextBtn}
                onClick={next}
              >
                {isLast ? 'Finish' : 'Next'}
              </Button>
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  )
}

export function useResetGuidedTour() {
  return () => localStorage.removeItem(TOUR_STORAGE_KEY)
}

export default GuidedTour